import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, query, where, orderBy, serverTimestamp, updateDoc, setDoc } from 'firebase/firestore';
import { showRechargeSuccess, showError, showWarning } from '../utils/notifications';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { createNewUserWithoutSession } from '../utils/authUtils'; // Importar la función de utilidad
import { logCreateCA, logLogout } from '../services/auditService';

function AddCA() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('vendors');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Form fields
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [cedula, setCedula] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [serial, setSerial] = useState('');
  const [zonaPrincipal, setZonaPrincipal] = useState('');
  const [subZona, setSubZona] = useState('');
  const [zona, setZona] = useState('No asignada');
  const [numberCa, setNumberCa] = useState('');
  const [initialBalance, setInitialBalance] = useState(0);
  const [tipoVehiculos, setTipoVehiculos] = useState('autos_y_motos');

  // Generar zonas principales (1-20)
  const zonasPrincipales = Array.from({length: 20}, (_, i) => ({
    value: i + 1,
    label: `Zona ${i + 1}`
  }));

  // Generar subzonas (01-100)
  const subZonas = Array.from({length: 100}, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: `${String(i + 1).padStart(2, '0')}`
  }));

  // Actualizar zona combinada cuando cambian zona principal o subzona
  useEffect(() => {
    if (zonaPrincipal && subZona) {
      setZona(`Z ${zonaPrincipal}-S${subZona}`);
    } else {
      setZona('No asignada');
    }
  }, [zonaPrincipal, subZona]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verificar si el perfil del usuario existe en Firestore
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          // El usuario existe, continuar normalmente
          setUser(currentUser);
          
          // Almacenar el rol del usuario
          const userData = userDoc.data();
          setUserRole(userData.rol || 'Usuario');
          
          // Verificar si tiene permisos de administrador, supervisor o recaudador
          if (userData.rol !== 'Admin' && userData.rol !== 'Supervisor' && userData.rol !== 'Recaudador') {
            showError("Acceso denegado", "No tienes permisos para crear Controladores Autorizados");
            navigate('/dashboard');
            return;
          }
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Generar número CA secuencial (CA01, CA02, etc.)
  useEffect(() => {
    const generateCANumber = async () => {
      try {
        // Buscar en la colección usuarios para encontrar el último CA
        const usuariosRef = collection(db, 'usuarios');
        const q = query(usuariosRef, where("rol", "==", "CA"), orderBy('numberCa', 'desc'));
        const querySnapshot = await getDocs(q);
        
        // Si hay documentos, tomamos el primero (mayor número CA) e incrementamos
        if (!querySnapshot.empty) {
          const latestCA = querySnapshot.docs[0].data().numberCa || '';
          const match = latestCA.match(/CA(\d+)/);
          
          if (match) {
            const lastNumber = parseInt(match[1], 10);
            const newNumber = lastNumber + 1;
            setNumberCa(`CA${newNumber.toString().padStart(2, '0')}`);
          } else {
            // Si no sigue el formato esperado, iniciamos desde CA01
            setNumberCa("CA01");
          }
        } else {
          // No hay CAs existentes, comenzamos desde CA01
          setNumberCa("CA01");
        }
      } catch (error) {
        console.error("Error al generar número CA:", error);
        // Fallback para desarrollo
        const randomNum = Math.floor(Math.random() * 99) + 1;
        setNumberCa(`CA${randomNum.toString().padStart(2, '0')}`);
      }
    };

    generateCANumber();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos
    if (!nombre || !email || !cedula || !telefono || !direccion || !serial || !numberCa) {
      showWarning("Campos incompletos", "Todos los campos son obligatorios");
      return;
    }
    
    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showWarning("Correo inválido", "Por favor ingresa un correo electrónico válido");
      return;
    }
    
    // Validar formato de cédula
    if (cedula.length < 7) {
      showWarning("Cédula inválida", "La cédula debe tener al menos 7 caracteres");
      return;
    }
    
    setSubmitting(true);
    
    try {
      // VERIFICAR DUPLICADOS ANTES DE CREAR
      console.log('🔍 Verificando duplicados...');
      
      // 1. Verificar si el correo ya existe en Firestore
      const emailCheckRef = doc(db, 'usuarios', email);
      const emailDoc = await getDoc(emailCheckRef);
      
      if (emailDoc.exists()) {
        throw new Error(`El correo electrónico ${email} ya está registrado en la base de datos. Si deseas actualizar los datos, usa la función de editar CA.`);
      }
      
      // 2. Verificar si el ID CA ya existe
      const usuariosRef = collection(db, 'usuarios');
      const caQuery = query(usuariosRef, where("numberCa", "==", numberCa));
      const caQuerySnapshot = await getDocs(caQuery);
      
      if (!caQuerySnapshot.empty) {
        throw new Error(`El ID de CA "${numberCa}" ya está en uso. Por favor elige otro ID.`);
      }
      
      // 3. Verificar si la cédula ya existe
      const cedulaQuery = query(usuariosRef, where("cedula", "==", cedula));
      const cedulaQuerySnapshot = await getDocs(cedulaQuery);
      
              if (!cedulaQuerySnapshot.empty) {
          throw new Error(`La cédula ${cedula} ya está registrada en el sistema`);
        }
        
        // 4. Verificar si el serial ya existe
        const serialQuery = query(usuariosRef, where("serial", "==", serial));
        const serialQuerySnapshot = await getDocs(serialQuery);
        
        if (!serialQuerySnapshot.empty) {
          throw new Error(`El serial ${serial} ya está registrado en el sistema`);
        }
        
        console.log('✅ Verificaciones completadas - No hay duplicados');
      
      // Convertir el saldo inicial a número con exactamente 2 decimales
      const initialBalanceNumber = parseFloat(parseFloat(initialBalance).toFixed(2));
      
      const createdAtString = new Date().toISOString();

      // Crear la estructura de datos exacta para el CA
      const userData = {
        balance: initialBalanceNumber,
        cedula: cedula,
        createdAt: createdAtString,
        direccion: direccion,
        email: email,
        nombre: nombre,
        numberCa: numberCa,
        rol: "CA",
        serial: serial,
        status: 0, // INACTIVO por defecto
        telefono: telefono,
        zona: zona || "No asignada",
        tipoVehiculos: tipoVehiculos
      };
      
      console.log('💾 Creando CA con datos:', userData);
      
      // Intentar crear el usuario completo (Firebase Auth + Firestore)
      let authAccountCreated = false;
      let successMessage = "";
      
      try {
        const result = await createNewUserWithoutSession(userData, cedula, user.email, true);
        
        if (result.success) {
          authAccountCreated = true;
          successMessage = `El Controlador Autorizado ${nombre} (${numberCa}) ha sido registrado correctamente como INACTIVO. La contraseña inicial es su cédula: ${cedula}. Recuerda activarlo desde la lista de CAs.`;
        } else {
          throw new Error(result.error);
        }
      } catch (authError) {
        console.log('⚠️ Error al crear cuenta en Firebase Auth:', authError.message);
        
        // Si el error es porque el email ya existe en Auth, solo crear en Firestore
        if (authError.message.includes('email ya está registrado en Firebase Auth') || 
            authError.message.includes('auth/email-already-in-use')) {
          
          console.log('📝 Email existe en Firebase Auth, creando solo en Firestore...');
          
          // Crear solo en Firestore sin cuenta de Auth
          await setDoc(doc(db, 'usuarios', email), {
            ...userData,
            createdAt: createdAtString,
            updatedAt: serverTimestamp(),
            createdBy: user.email,
            authAccountExists: true, // Marcar que ya tiene cuenta de Auth
            needsAuthAccount: false
          });
          
          authAccountCreated = false;
          successMessage = `El Controlador Autorizado ${nombre} (${numberCa}) ha sido registrado en la base de datos. El email ya tenía una cuenta de Firebase Auth existente. Recuerda activarlo desde la lista de CAs.`;
        } else {
          // Si es otro tipo de error, relanzarlo
          throw authError;
        }
      }
      
      // Agregar los datos específicos que faltan después de crear el usuario
      await updateDoc(doc(db, 'usuarios', email), {
        lastRecharge: {
          amount: 0,
          by: user.email,
          date: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
      
      // Registrar la creación en logs de auditoría
      await logCreateCA(
        {email: user.email, rol: userRole},
        { ...userData, email, authAccountCreated }
      );
      
      // Mostrar mensaje de éxito
      showRechargeSuccess(
        "CA Creado Exitosamente",
        successMessage
      );
      
      // Redirigir a la lista de CAs
      navigate('/vendors');
    } catch (error) {
      console.error("Error al crear CA:", error);
      showError("Error", `No se pudo crear el Controlador Autorizado: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Agregar CA" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
      
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
        {/* Top Header */}
        <header className="bg-white shadow px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-900 ml-8 md:ml-0">Agregar Controlador Autorizado</h1>
            <div className="text-right flex items-center">
              <div className="mr-4">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">{userRole}</p>
              </div>
              <button              onClick={async () => {
                  try {
                    // Registrar cierre de sesión en logs
                    await logLogout({email: user?.email, rol: userRole}, {
                      reason: 'manual',
                      userAgent: navigator.userAgent,
                      platform: navigator.platform,
                      timestamp: new Date().toISOString()
                    });
                    localStorage.removeItem('userRole');
                    await auth.signOut();
                    navigate('/login');
                  } catch (error) {
                    console.error("Error al cerrar sesión:", error);
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-6">Datos del nuevo Controlador Autorizado</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                  <input
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Carlos Pérez"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: carlos@example.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                  <input
                    type="text"
                    value={cedula}
                    onChange={(e) => setCedula(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 1712345678"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La cédula será usada como contraseña inicial
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 0991234567"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Av. Principal 123, Centro"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial del Dispositivo</label>
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: SN123456789"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Número de serie único del dispositivo asignado
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID de CA</label>
                  <input
                    type="text"
                    value={numberCa}
                    onChange={(e) => setNumberCa(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: CA01, CA59, etc."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puedes modificar el ID sugerido si es necesario
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Saldo Inicial</label>
                  <input
                    type="number"
                    value={initialBalance}
                    onChange={(e) => setInitialBalance(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Vehículos</label>
                  <select
                    value={tipoVehiculos}
                    onChange={(e) => setTipoVehiculos(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="autos_y_motos">🚗🏍️ Autos y Motos</option>
                    <option value="solo_autos">🚗 Solo Autos</option>
                    <option value="solo_motos">🏍️ Solo Motos</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecciona qué tipo de vehículos puede procesar este CA
                  </p>
                </div>
              </div>
              
              {/* Sección de Zonas */}
              <div className="border-t pt-4 mt-6">
                <h3 className="text-md font-medium text-gray-800 mb-4">📍 Asignación de Zona</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona Principal</label>
                    <select
                      value={zonaPrincipal}
                      onChange={(e) => setZonaPrincipal(e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecciona zona principal</option>
                      {zonasPrincipales.map((zona) => (
                        <option key={zona.value} value={zona.value}>
                          {zona.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subzona</label>
                    <select
                      value={subZona}
                      onChange={(e) => setSubZona(e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={!zonaPrincipal}
                    >
                      <option value="">Selecciona subzona</option>
                      {subZonas.map((zona) => (
                        <option key={zona.value} value={zona.value}>
                          {zona.label}
                        </option>
                      ))}
                    </select>
                    {!zonaPrincipal && (
                      <p className="text-xs text-gray-400 mt-1">
                        Primero selecciona una zona principal
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona Final</label>
                    <input
                      type="text"
                      value={zona}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-600"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Se genera automáticamente
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Nota informativa */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mt-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-400 text-xl">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Información importante
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>El CA se creará con estado <strong>INACTIVO</strong> por defecto</li>
                        <li>La contraseña inicial será la cédula ingresada (si se crea cuenta nueva)</li>
                        <li>Si el email ya existe en Firebase Auth, solo se registrarán los datos en la base de datos</li>
                        <li>Si el email no existe, se creará cuenta completa (Auth + Base de datos)</li>
                        <li>Deberás activar el CA desde la lista de Controladores</li>
                        <li>Se verificará que no existan duplicados de ID CA, cédula y serial</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/vendors')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {submitting ? 'Creando...' : 'Crear Controlador Autorizado'}
                </button>
              </div>
            </form>
          </div>
        </main>
        </div>
      </div>
    </div>
  );
}

export default AddCA;
